import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ZoomIn, ZoomOut, Maximize2, Search } from 'lucide-react';
import { registryApi } from '../../services/registryService';
import * as d3Force from 'd3-force';
import * as d3Selection from 'd3-selection';
import * as d3Zoom from 'd3-zoom';
import * as d3Drag from 'd3-drag';

const TIER_COLORS = {
  0: '#ef4444', // red
  1: '#f97316', // orange
  2: '#eab308', // yellow
  3: '#3b82f6', // blue
  4: '#9ca3af', // gray
};

const TIER_LABELS = {
  0: 'Tier 0 — Critical',
  1: 'Tier 1 — Core',
  2: 'Tier 2 — Integration',
  3: 'Tier 3 — Domain',
  4: 'Tier 4 — Edge',
};

export default function DependencyMap({ onNavigate }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const simulationRef = useRef(null);
  const [graphData, setGraphData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [hoveredNode, setHoveredNode] = useState(null);
  const [tierFilter, setTierFilter] = useState('all');

  useEffect(() => {
    const fetchGraph = async () => {
      try {
        const result = await registryApi.getGraph();
        setGraphData(result.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchGraph();
  }, []);

  const renderGraph = useCallback(() => {
    if (!graphData || !svgRef.current || !containerRef.current) return;

    const container = containerRef.current;
    const width = container.clientWidth;
    const height = Math.max(500, container.clientHeight);

    // Filter nodes
    let nodes = graphData.nodes.map((n) => ({ ...n }));
    if (tierFilter !== 'all') {
      const tierNum = parseInt(tierFilter, 10);
      // Show filtered tier + their connected nodes
      const filteredIds = new Set(nodes.filter((n) => n.tier === tierNum).map((n) => n.service_id));
      graphData.edges.forEach((e) => {
        if (filteredIds.has(e.source) || filteredIds.has(e.target)) {
          filteredIds.add(e.source);
          filteredIds.add(e.target);
        }
      });
      nodes = nodes.filter((n) => filteredIds.has(n.service_id));
    }

    const nodeIds = new Set(nodes.map((n) => n.service_id));
    const edges = graphData.edges
      .filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target))
      .map((e) => ({ ...e }));

    // Circular dep set for styling
    const circularSet = new Set();
    (graphData.circular_dependencies || []).forEach((c) => {
      circularSet.add(`${c.services[0]}->${c.services[1]}`);
      circularSet.add(`${c.services[1]}->${c.services[0]}`);
    });

    // Clear previous
    const svg = d3Selection.select(svgRef.current);
    svg.selectAll('*').remove();

    // Setup zoom
    const g = svg.append('g');
    const zoom = d3Zoom.zoom()
      .scaleExtent([0.2, 4])
      .on('zoom', (event) => g.attr('transform', event.transform));
    svg.call(zoom);

    // Arrow marker
    svg.append('defs').append('marker')
      .attr('id', 'arrowhead')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 28)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,-5L10,0L0,5')
      .attr('fill', '#94a3b8');

    // Simulation
    const simulation = d3Force.forceSimulation(nodes)
      .force('link', d3Force.forceLink(edges).id((d) => d.service_id).distance(120))
      .force('charge', d3Force.forceManyBody().strength(-400))
      .force('center', d3Force.forceCenter(width / 2, height / 2))
      .force('collision', d3Force.forceCollide().radius(35));

    simulationRef.current = simulation;

    // Edges
    const link = g.append('g')
      .selectAll('line')
      .data(edges)
      .join('line')
      .attr('stroke', (d) => {
        const key = `${typeof d.source === 'object' ? d.source.service_id : d.source}->${typeof d.target === 'object' ? d.target.service_id : d.target}`;
        return circularSet.has(key) ? '#ef4444' : '#cbd5e1';
      })
      .attr('stroke-width', (d) => {
        const key = `${typeof d.source === 'object' ? d.source.service_id : d.source}->${typeof d.target === 'object' ? d.target.service_id : d.target}`;
        return circularSet.has(key) ? 2 : 1;
      })
      .attr('stroke-dasharray', (d) => {
        const key = `${typeof d.source === 'object' ? d.source.service_id : d.source}->${typeof d.target === 'object' ? d.target.service_id : d.target}`;
        return circularSet.has(key) ? '5,3' : 'none';
      })
      .attr('marker-end', 'url(#arrowhead)');

    // Nodes
    const node = g.append('g')
      .selectAll('g')
      .data(nodes)
      .join('g')
      .style('cursor', 'pointer')
      .call(d3Drag.drag()
        .on('start', (event, d) => {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', (event, d) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', (event, d) => {
          if (!event.active) simulation.alphaTarget(0);
          d.fx = null;
          d.fy = null;
        }));

    node.append('circle')
      .attr('r', (d) => {
        const depCount = graphData.edges.filter((e) => e.target === d.service_id || (typeof e.target === 'object' && e.target.service_id === d.service_id)).length;
        return Math.max(8, Math.min(20, 8 + depCount * 2));
      })
      .attr('fill', (d) => TIER_COLORS[d.tier] || TIER_COLORS[4])
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .attr('opacity', 0.9);

    node.append('text')
      .text((d) => d.name.replace(/-service$/, '').replace(/-pipeline$/, ''))
      .attr('dy', -14)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('fill', '#374151')
      .attr('font-weight', '500');

    // Hover interactions
    node.on('mouseover', function (event, d) {
      setHoveredNode(d);
      // Dim unconnected
      const connectedIds = new Set([d.service_id]);
      edges.forEach((e) => {
        const srcId = typeof e.source === 'object' ? e.source.service_id : e.source;
        const tgtId = typeof e.target === 'object' ? e.target.service_id : e.target;
        if (srcId === d.service_id) connectedIds.add(tgtId);
        if (tgtId === d.service_id) connectedIds.add(srcId);
      });
      node.attr('opacity', (n) => connectedIds.has(n.service_id) ? 1 : 0.2);
      link.attr('opacity', (l) => {
        const srcId = typeof l.source === 'object' ? l.source.service_id : l.source;
        const tgtId = typeof l.target === 'object' ? l.target.service_id : l.target;
        return (srcId === d.service_id || tgtId === d.service_id) ? 1 : 0.1;
      });
    })
    .on('mouseout', function () {
      setHoveredNode(null);
      node.attr('opacity', 1);
      link.attr('opacity', 1);
    })
    .on('click', (event, d) => {
      onNavigate(`registry-detail:${d.service_id}`);
    });

    // Tick
    simulation.on('tick', () => {
      link
        .attr('x1', (d) => d.source.x)
        .attr('y1', (d) => d.source.y)
        .attr('x2', (d) => d.target.x)
        .attr('y2', (d) => d.target.y);
      node.attr('transform', (d) => `translate(${d.x},${d.y})`);
    });

    // Search highlight
    if (searchTerm) {
      node.select('circle').attr('stroke', (d) =>
        d.name.toLowerCase().includes(searchTerm.toLowerCase()) ? '#f59e0b' : '#fff'
      ).attr('stroke-width', (d) =>
        d.name.toLowerCase().includes(searchTerm.toLowerCase()) ? 4 : 2
      );
    }

    return () => simulation.stop();
  }, [graphData, tierFilter, searchTerm, onNavigate]);

  useEffect(() => { renderGraph(); }, [renderGraph]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return <div className="bg-red-50 text-red-700 p-4 rounded-lg">{error}</div>;
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900">Dependency Map</h2>
        <p className="text-sm text-gray-500 mt-1">
          {graphData?.stats?.total_services} services, {graphData?.stats?.total_edges} edges, {graphData?.stats?.circular_count} circular dependencies
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[180px] max-w-[300px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search nodes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400 outline-none"
          />
        </div>
        <select
          value={tierFilter}
          onChange={(e) => setTierFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400 outline-none"
        >
          <option value="all">All Tiers</option>
          {Object.entries(TIER_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        {Object.entries(TIER_COLORS).map(([tier, color]) => (
          <div key={tier} className="flex items-center gap-1.5 text-xs text-gray-600">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
            T{tier}
          </div>
        ))}
        <div className="flex items-center gap-1.5 text-xs text-gray-600">
          <span className="w-6 border-t-2 border-dashed border-red-400" />
          Circular
        </div>
      </div>

      {/* Graph */}
      <div ref={containerRef} className="bg-white rounded-xl border border-gray-200 overflow-hidden" style={{ height: '600px' }}>
        <svg ref={svgRef} width="100%" height="100%" />
      </div>

      {/* Hover tooltip */}
      {hoveredNode && (
        <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200 text-sm">
          <span className="font-semibold text-gray-900">{hoveredNode.name}</span>
          <span className="text-gray-400 ml-2">Tier {hoveredNode.tier ?? '—'}</span>
          <span className="text-gray-400 ml-2">{hoveredNode.component_count} components</span>
          <p className="text-gray-500 text-xs mt-1 truncate">{hoveredNode.capability}</p>
        </div>
      )}
    </div>
  );
}
